import { useEffect, useRef } from 'react'
import { storageGet, storageSet } from '@/lib/storage'
import { snapshotKey } from '@/lib/constants'

/**
 * Persists the last successful snapshot payload to localStorage.
 * Returns the persisted fallback only if it is recent enough (< 30 min).
 */
export function usePersistedSnapshot<T extends { updatedAt?: string }>(
  moduleId: string,
  endpoint: string,
  liveData: T | undefined
): { persisted: T | null } {
  const key = snapshotKey(moduleId, endpoint)
  const raw = storageGet<T>(key)

  // Reject persisted data older than 30 minutes
  const MAX_AGE_MS = 30 * 60 * 1000
  const isRecent = raw?.updatedAt
    ? Date.now() - new Date(raw.updatedAt).getTime() < MAX_AGE_MS
    : false

  const persistedRef = useRef<T | null>(isRecent ? raw : null)

  useEffect(() => {
    if (liveData !== undefined) {
      persistedRef.current = liveData
      storageSet(key, liveData)
    }
  }, [liveData, key])

  return { persisted: persistedRef.current }
}
